import React, { ReactNode } from "react";
import { CustomIcon } from "../../components/Icons";
import i18n from "../../i18n";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { roundFloat } from "../../utils/round-float";
import BuildingService from "./BuildingService";

type CostSet = {
  discount: number;
  clegCost?: number;
  stoneCost?: number;
  ironCost?: number;
};

/**
 * Pulls the build price out of a building record. Most buildings carry it on the
 * list row under `firstLevelBuildingInfo`, but some — the Research building among
 * them — only expose it on the detail endpoint, as the cost of reaching level 1.
 */
const readBuildCost = (item: any): CostSet => {
  const info = item?.firstLevelBuildingInfo ?? item?.buildInfo ?? item;
  return {
    discount: item?.discount ?? 0,
    clegCost:
      info?.clegCost ??
      info?.buildClegCost ??
      info?.buildCost ??
      item?.upgradeCost,
    stoneCost:
      info?.stoneCost ?? info?.buildStoneCost ?? item?.upgradeStoneCost,
    ironCost: info?.ironCost ?? info?.buildIronCost ?? item?.upgradeIronCost,
  };
};

const hasBuildCost = (cost: CostSet) =>
  Boolean(cost.clegCost || cost.stoneCost || cost.ironCost);

const CostRow: React.FC<{ cost: CostSet }> = ({ cost }) => {
  const { t } = useAppTranslation("village");
  const renderCost = (icon: "cleg" | "stone" | "metal", amount?: number) => {
    if (!amount) return null;
    if (cost.discount > 0) {
      return (
        <div className="d-flex ms-2 gap-1 align-items-center">
          <CustomIcon icon={icon} width={24} height={24} className="ms-1" />
          <s className="fs-tiny">{roundFloat(amount, 3)}</s>
          <b className="text-success-light">
            {roundFloat((amount * (100 - cost.discount)) / 100, 3)}
          </b>
        </div>
      );
    }
    return (
      <div className="d-flex ms-2 align-items-center">
        <CustomIcon icon={icon} width={24} height={24} className="ms-1" />
        {roundFloat(amount, 3)}
      </div>
    );
  };

  return (
    <div className="d-flex mt-3 gap-2 flex-wrap align-items-end">
      <div>{t("village:buildCost.label")}</div>
      {renderCost("cleg", cost.clegCost)}
      {renderCost("stone", cost.stoneCost)}
      {renderCost("metal", cost.ironCost)}
    </div>
  );
};

/**
 * Renders the resource cost breakdown shown in the "Build Confirmation" dialog.
 * Returns null when there is no cost information to display.
 */
export const renderBuildCost = (item: any) => {
  if (!item) return null;
  const cost = readBuildCost(item);
  if (!hasBuildCost(cost)) return null;
  return <CostRow cost={cost} />;
};

/**
 * Build cost for the confirm dialog opened straight from the map, where only the
 * building list row is on hand. When that row carries no price — the Research
 * building — it fills the gap from the detail endpoint instead of showing nothing.
 */
export const BuildCostPreview: React.FC<{ building: any }> = ({ building }) => {
  const { t } = useAppTranslation("village");
  const [detailCost, setDetailCost] = React.useState<CostSet | null>(null);
  const [loading, setLoading] = React.useState(false);

  const listCost = readBuildCost(building);
  const needsDetail = !hasBuildCost(listCost) && Boolean(building?.id);
  const buildingId = building?.id;

  React.useEffect(() => {
    if (!needsDetail) return;

    let cancelled = false;
    setLoading(true);

    BuildingService.getBuildingDetail(buildingId)
      .then((response: any) => {
        if (cancelled || !response?.ok || !response?.data) return;
        setDetailCost(readBuildCost({ ...building, ...response.data }));
      })
      .catch((error) => {
        console.error("Error fetching build cost:", error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, needsDetail]);

  const cost = detailCost ?? listCost;

  if (!hasBuildCost(cost)) {
    return loading ? (
      <div className="d-flex mt-3 fs-tiny">{t("village:buildCost.loading")}</div>
    ) : null;
  }

  return <CostRow cost={cost} />;
};

// Called outside render (from a click handler), so it reads the language from i18n itself.
const costAmount = (resource: "cleg" | "stone" | "iron", amount: number) =>
  i18n.t("village:buildCost.amount", {
    resource: i18n.t(`village:resources.${resource}`),
    amount: amount.toFixed(2),
  });

/**
 * Builds the list of cost nodes shown in the "Instant Upgrade" confirm dialog.
 * Only includes a resource when its cost is greater than zero.
 */
export const buildInstantUpgradeCostParts = (
  clegCost: number,
  stoneCost: number,
  ironCost: number
): ReactNode[] => {
  const costParts: ReactNode[] = [];
  if (clegCost > 0)
    costParts.push(
      <>
        <CustomIcon icon="cleg" width={30} height={30} className="ms-1" />
        {costAmount("cleg", clegCost)}
      </>
    );
  if (stoneCost > 0)
    costParts.push(
      <>
        <CustomIcon
          icon="stone"
          width={30}
          height={30}
          className="ms-1"
        />
        {costAmount("stone", stoneCost)}
      </>
    );
  if (ironCost > 0)
    costParts.push(
      <>
        <CustomIcon
          icon="metal"
          width={30}
          height={30}
          className="ms-1"
        />
        {costAmount("iron", ironCost)}
      </>
    );
  return costParts;
};
